import { parse } from 'date-fns/parse';
import { type ConfigObject, Lang } from '../../../../_configs/types';
import type { Car } from '../../../../_types/Car';
import type { Modules } from '../../../../modules/_definition/types';
import stringToSlug from '../../../../utils/string-to-slug';
import checkUndefinedNullOrEmpty from '../../../../utils/tools/checkUndefinedNullOrEmpty';
import checkUndefinedOrNull from '../../../../utils/tools/checkUndefinedOrNull';
import type { FaarenListCar } from '../../../types/faaren';
import { getFaarenPriceList } from '../../faaren/getFaarenPriceList';
import handleCarList from './handleCarList';
import prepareAccessoires from './prepareAccessoires';
import prepareCarData from './prepareCarData';

let abortControllerFaaren: AbortController;

export default async function handleCar(car: Car<true>, module: Modules, settings: ConfigObject, faarenVehicles: FaarenListCar[], loadPricePlans: boolean): Promise<Car<false>> {
    if (checkUndefinedNullOrEmpty(car)) {
        return undefined;
    } else {
        const { bilderUrl, bilderUrlHD, bilderUrlS, usersSelect, verbrauch } = prepareCarData(car, settings);
        const faarenVehicle = faarenVehicles?.find((v) => v?.external_id === car?.ident);

        let faarenPriceList = [];
        try {
            abortControllerFaaren?.abort();
            abortControllerFaaren = new AbortController();
            faarenPriceList = (
                !checkUndefinedNullOrEmpty(faarenVehicle?.price_plans) && loadPricePlans === true
                    ? (await getFaarenPriceList(faarenVehicle?.price_plans, settings?.api?.faaren?.apiKey, abortControllerFaaren?.signal, settings?.general?.debug))?.price_plans
                    : []
            )?.map((v) => ({ ...v, price: v?.price / 100 }));
            abortControllerFaaren = null;
        } catch (err) {
            console.error(err);
        }

        return {
            aktion: car?.aktion === '1',
            antrieb: car?.antrieb,
            aufbau: car?.aufbau,
            ausstattung: prepareAccessoires(car?.ausstattung),
            autoAbo: !checkUndefinedNullOrEmpty(faarenVehicle)
                ? {
                      additional_milage_costs: (faarenVehicle?.additional_mileage_cost || 0) / 100,
                      damage_participation: parseFloat(faarenVehicle?.damage_participation?.replace(/(\s|\.|€)+/g, '')?.replace(',', '.')),
                      delivery_option: [faarenVehicle?.delivery_option],
                      delivery_time_after_booking: faarenVehicle?.delivery_time_after_booking || 0,
                      fixed_duration: faarenVehicle?.fixed_duration === 1,
                      immediately_available: faarenVehicle?.immediately_available === 1,
                      min_license_duration: faarenVehicle?.min_license_duration || 0,
                      minimum_age: parseInt(faarenVehicle?.minimum_age?.replace(/[A-z\s]+/g, '') || '0'),
                      pick_up_option: [faarenVehicle?.pick_up_option],
                      plans: faarenPriceList,
                  }
                : undefined,
            bilder: {
                '360': car?.bilder?.['360'],
                '360a': car?.bilder?.['360a'],
                '360a_h': car?.bilder?.['360a_h'],
                '360a_w': car?.bilder?.['360a_w'],
                'anz360a': car?.bilder?.anz360a,
                'bilder': car?.bilder?.bilder,
                'ec': car?.bilder?.ec,
                'hd': car?.bilder?.hd,
                'pano': car?.bilder?.pano,
                'url': bilderUrl,
                'urlHD': bilderUrlHD,
                'urlS': bilderUrlS,
            },
            desc: {
                assistenz: car?.desc?.assistenz,
                aussen: car?.desc?.aussen,
                finanzierung: car?.desc?.finanzierung,
                fuss: car?.desc?.fuss,
                fussnoten: car?.desc?.fussnoten,
                garantie: car?.desc?.garantie,
                highlights: car?.desc?.highlights,
                innen: car?.desc?.innen,
                isList: car?.desc?.isList,
                kopf: car?.desc?.kopf,
                multimedia: car?.desc?.multimedia,
                pakete: car?.desc?.pakete,
                preissenkung: car?.desc?.preissenkung,
                raeder: car?.desc?.raeder,
                scheinwerfer: car?.desc?.scheinwerfer,
                sitze: car?.desc?.sitze,
                sonstiges: car?.desc?.sonstiges,
                technik: car?.desc?.technik,
            },
            design: car?.design,
            engine: {
                effizienz: car?.engine?.effizienz !== '(unbekannt)' ? car?.engine?.effizienz : undefined,
                envkv: car?.engine?.envkv,
                hubraum: car?.engine?.hubraum,
                kraftstoff: car?.engine?.kraftstoff,
                kraftstoff_1: car?.engine?.kraftstoff_1,
                leistung: `${car?.engine?.leistung?.[1] || '-'} PS`,
                leistungKw: `${car?.engine?.leistung?.[0] || '-'} kW`,
                leistungKwNumbered: car?.engine?.leistung?.[0],
                motor: car?.engine?.motor?.split(' ')?.[0],
                motorTooltip: car?.engine?.motor?.split(' ')?.[1],
                plakette: car?.engine?.plakette,
                schadstoff: car?.engine?.schadstoff,
            },
            ez: !checkUndefinedOrNull(car?.ez) ? parse(car?.ez, 'dd.MM.yyyy', new Date()) : undefined,
            fahrbereit: car?.fahrbereit,
            farbe: { grund: car?.farbe?.[0], name: car?.farbe?.[1] },
            farbe_innen: { grund: car?.farbe_innen?.[0], name: car?.farbe_innen?.[1] },
            finanzierung:
                settings?.general?.showFinanzierungen === true && !checkUndefinedNullOrEmpty(car?.finanzierung)
                    ? {
                          abschlussgebühr: car?.finanzierung?.abschlussgebühr,
                          anbieterbank: car?.finanzierung?.anbieterbank,
                          anzahlung: !checkUndefinedNullOrEmpty(car?.finanzierung?.anzahlung) ? Math.round(car?.finanzierung?.anzahlung) : null,
                          art_sollzinssatzes: car?.finanzierung?.art_sollzinssatzes,
                          bruttokreditbetrag: car?.finanzierung?.bruttokreditbetrag,
                          effektiver_jahreszins: car?.finanzierung?.effektiver_jahreszins,
                          km: !checkUndefinedNullOrEmpty(car?.finanzierung?.km) ? parseFloat(car?.finanzierung?.km) : undefined,
                          ksb: car?.finanzierung?.ksb,
                          ksb_betrag: !checkUndefinedNullOrEmpty(car?.finanzierung?.ksb_betrag) ? parseFloat(car?.finanzierung?.ksb_betrag) : undefined,
                          laufzeit: car?.finanzierung?.laufzeit,
                          monatliche_rate: car?.finanzierung?.monatliche_rate,
                          monatliche_rate_netto: car?.finanzierung?.monatliche_rate_netto,
                          nettrokreditbetrag: !checkUndefinedNullOrEmpty(car?.finanzierung?.nettrokreditbetrag) ? parseFloat(car?.finanzierung?.nettrokreditbetrag) : undefined,
                          schlussrate: car?.finanzierung?.schlussrate,
                          soll_zinssatz: !checkUndefinedNullOrEmpty(car?.finanzierung?.soll_zinssatz) ? parseFloat(car?.finanzierung?.soll_zinssatz) : undefined,
                          text1: car?.finanzierung?.text1,
                          text2: car?.finanzierung?.text2,
                      }
                    : undefined,
            Firma: car?.Firma,
            firmaAlias: car?.firmaAlias,
            firmaName: car?.firmaName,
            garantie: { id: car?.garantie?.[0], name: car?.garantie?.[1] },
            getriebe: !checkUndefinedNullOrEmpty(car?.getriebe)
                ? settings?.general?.language === Lang.deDE
                    ? car?.getriebe?.substring(0, car?.getriebe.indexOf(' ') > 0 ? car?.getriebe.indexOf(' ') : undefined)
                    : car?.getriebe
                : undefined,
            herkunft: car?.herkunft,
            hersteller: car?.hersteller,
            hsn: car?.hsn,
            hu: !checkUndefinedOrNull(car?.hu) ? parse(car?.hu, 'dd.MM.yyyy', new Date()) : undefined,
            id: car?.id,
            ident: car?.ident,
            idFirma: car?.idFirma,
            idStandort: car?.idStandort,
            is_eyecatcher: car?.is_eyecatcher,
            is_id_firmenimage: car?.is_id_firmenimage,
            is_id_logo: car?.is_id_logo,
            kategorie: car?.kategorie,
            km: car?.km,
            land: car?.land,
            leasing:
                settings?.general?.showLeasing === true && !checkUndefinedNullOrEmpty(car?.leasing)
                    ? {
                          anbieterbank: car?.leasing?.anbieterbank,
                          anzahlung: car?.leasing?.anzahlung,
                          DestinationCharges: !checkUndefinedNullOrEmpty(car?.leasing?.DestinationCharges) ? parseFloat(car?.leasing?.DestinationCharges) : undefined,
                          effektiver_jahreszins: !checkUndefinedNullOrEmpty(car?.leasing?.effektiver_jahreszins) ? parseFloat(car?.leasing?.effektiver_jahreszins) : undefined,
                          ExtraMileageCosts: !checkUndefinedNullOrEmpty(car?.leasing?.ExtraMileageCosts) ? parseFloat(car?.leasing?.ExtraMileageCosts) / 100 : undefined,
                          km: !checkUndefinedNullOrEmpty(car?.leasing?.km) ? parseFloat(car?.leasing?.km) : undefined,
                          laufzeit: !checkUndefinedNullOrEmpty(car?.leasing?.laufzeit) ? parseFloat(car?.leasing?.laufzeit) : undefined,
                          LowMileageCompensation: !checkUndefinedNullOrEmpty(car?.leasing?.LowMileageCompensation) ? parseFloat(car?.leasing?.LowMileageCompensation) / 100 : undefined,
                          monatliche_rate: !checkUndefinedNullOrEmpty(car?.leasing?.monatliche_rate) ? parseFloat(car?.leasing?.monatliche_rate) : undefined,
                          monatliche_rate_netto: !checkUndefinedNullOrEmpty(car?.leasing?.monatliche_rate_netto)
                              ? parseFloat(car?.leasing?.monatliche_rate_netto)
                              : !checkUndefinedNullOrEmpty(car?.leasing?.monatliche_rate)
                                ? car?.preis?.mwst_satz > 0
                                    ? parseFloat(car?.leasing?.monatliche_rate) / ((100 + car?.preis?.mwst_satz) / 100)
                                    : parseFloat(car?.leasing?.monatliche_rate)
                                : undefined,
                          nettrokreditbetrag: !checkUndefinedNullOrEmpty(car?.leasing?.nettrokreditbetrag) ? parseFloat(car?.leasing?.nettrokreditbetrag) : undefined,
                          RegistrationFees: !checkUndefinedNullOrEmpty(car?.leasing?.RegistrationFees) ? parseFloat(car?.leasing?.RegistrationFees) : undefined,
                          soll_zinssatz: !checkUndefinedNullOrEmpty(car?.leasing?.soll_zinssatz) ? parseFloat(car?.leasing?.soll_zinssatz) : undefined,
                          TotalAmount: !checkUndefinedNullOrEmpty(car?.leasing?.TotalAmount) ? parseFloat(car?.leasing?.TotalAmount) : undefined,
                          vk: !checkUndefinedNullOrEmpty(car?.leasing?.vk) ? car?.leasing?.vk : undefined,
                          zielgruppe: 'PRIVATE',
                      }
                    : undefined,
            leasingComm:
                settings?.general?.showLeasing === true && !checkUndefinedNullOrEmpty(car?.leasingComm)
                    ? {
                          anbieterbank: car?.leasingComm?.anbieterbank,
                          anzahlung: car?.leasingComm?.anzahlung,
                          DestinationCharges: !checkUndefinedNullOrEmpty(car?.leasingComm?.DestinationCharges) ? parseFloat(car?.leasingComm?.DestinationCharges) : undefined,
                          effektiver_jahreszins: !checkUndefinedNullOrEmpty(car?.leasingComm?.effektiver_jahreszins) ? parseFloat(car?.leasingComm?.effektiver_jahreszins) : undefined,
                          ExtraMileageCosts: !checkUndefinedNullOrEmpty(car?.leasingComm?.ExtraMileageCosts) ? parseFloat(car?.leasingComm?.ExtraMileageCosts) / 100 : undefined,
                          km: !checkUndefinedNullOrEmpty(car?.leasingComm?.km) ? parseFloat(car?.leasingComm?.km) : undefined,
                          laufzeit: !checkUndefinedNullOrEmpty(car?.leasingComm?.laufzeit) ? parseFloat(car?.leasingComm?.laufzeit) : undefined,
                          LowMileageCompensation: !checkUndefinedNullOrEmpty(car?.leasingComm?.LowMileageCompensation) ? parseFloat(car?.leasingComm?.LowMileageCompensation) / 100 : undefined,
                          monatliche_rate: checkUndefinedNullOrEmpty(car?.leasingComm?.monatliche_rate_netto)
                              ? !checkUndefinedNullOrEmpty(car?.leasingComm?.monatliche_rate)
                                  ? car?.preis?.mwst_satz > 0
                                      ? parseFloat(car?.leasingComm?.monatliche_rate) * ((100 + car?.preis?.mwst_satz) / 100)
                                      : parseFloat(car?.leasingComm?.monatliche_rate)
                                  : undefined
                              : parseFloat(car?.leasingComm?.monatliche_rate),
                          monatliche_rate_netto: !checkUndefinedNullOrEmpty(car?.leasingComm?.monatliche_rate_netto)
                              ? parseFloat(car?.leasingComm?.monatliche_rate_netto)
                              : !checkUndefinedNullOrEmpty(car?.leasingComm?.monatliche_rate)
                                ? parseFloat(car?.leasingComm?.monatliche_rate)
                                : undefined,
                          nettrokreditbetrag: !checkUndefinedNullOrEmpty(car?.leasingComm?.nettrokreditbetrag) ? parseFloat(car?.leasingComm?.nettrokreditbetrag) : undefined,
                          RegistrationFees: !checkUndefinedNullOrEmpty(car?.leasingComm?.RegistrationFees) ? parseFloat(car?.leasingComm?.RegistrationFees) : undefined,
                          soll_zinssatz: !checkUndefinedNullOrEmpty(car?.leasingComm?.soll_zinssatz) ? parseFloat(car?.leasingComm?.soll_zinssatz) : undefined,
                          TotalAmount: !checkUndefinedNullOrEmpty(car?.leasingComm?.TotalAmount) ? parseFloat(car?.leasingComm?.TotalAmount) : undefined,
                          vk: !checkUndefinedNullOrEmpty(car?.leasing?.vk) ? car?.leasing?.vk : undefined,
                          zielgruppe: 'COMMERCIAL',
                      }
                    : undefined,
            lHighlights: car?.lHighlights,
            lieferzeit: car?.lieferzeit,
            loc: {
                alias: car?.loc?.alias,
                firmaName: car?.loc?.firmaName,
                hm: car?.loc?.hm,
                id: car?.loc?.id,
                image: car?.loc?.image,
                mail: car?.loc?.mail,
                name: car?.loc?.name,
                openMo: car?.loc?.openMo,
                openDi: car?.loc?.openDi,
                openMi: car?.loc?.openMi,
                openDo: car?.loc?.openDo,
                openFr: car?.loc?.openFr,
                openSa: car?.loc?.openSa,
                openSo: car?.loc?.openSo,
                openServiceMo: car?.loc?.openServiceMo,
                openServiceDi: car?.loc?.openServiceDi,
                openServiceMi: car?.loc?.openServiceMi,
                openServiceDo: car?.loc?.openServiceDo,
                openServiceFr: car?.loc?.openServiceFr,
                openServiceSa: car?.loc?.openServiceSa,
                openServiceSo: car?.loc?.openServiceSo,
                openWerkstattMo: car?.loc?.openWerkstattMo,
                openWerkstattDi: car?.loc?.openWerkstattDi,
                openWerkstattMi: car?.loc?.openWerkstattMi,
                openWerkstattDo: car?.loc?.openWerkstattDo,
                openWerkstattFr: car?.loc?.openWerkstattFr,
                openWerkstattSa: car?.loc?.openWerkstattSa,
                openWerkstattSo: car?.loc?.openWerkstattSo,
                ort: car?.loc?.ort,
                plz: car?.loc?.plz,
                str: car?.loc?.str,
                tel: car?.loc?.tel,
                tel_hpt: car?.loc?.tel_hpt,
            },
            mod_g: car?.mod_g,
            mod_o: car?.mod_o,
            modelkey: car?.modelkey,
            modelkey6: car?.modelkey6,
            modell: car?.modell,
            modelljahr: !checkUndefinedOrNull(car?.modelljahr) ? parse(car?.modelljahr?.toString(), 'yyyy', new Date()) : undefined,
            name: car?.name,
            nameSlug: stringToSlug(car?.name),
            nfz: car?.nfz,
            polster: car?.polster,
            preis: {
                mwst: car?.preis?.mwst,
                mwst_satz: car?.preis?.mwst_satz,
                transport: car?.preis?.transport,
                upe: car?.preis?.upe,
                vk:
                    settings?.general?.showUpeWithTransferInsteadOfVk === true
                        ? !checkUndefinedNullOrEmpty(car?.preis?.upe)
                            ? (car?.preis?.upe || 0) + (car?.preis?.transport || 0)
                            : undefined
                        : car?.preis?.vk,
                vkn:
                    settings?.general?.showNettoUpeWithTransferInsteadOfVkn === true
                        ? !checkUndefinedNullOrEmpty(car?.preis?.upe) && !checkUndefinedNullOrEmpty(car?.preis?.mwst)
                            ? (car?.preis?.upe || 0) + (car?.preis?.transport || 0) - ((car?.preis?.upe || 0) + (car?.preis?.transport || 0)) * (car?.preis?.mwst_satz / 100)
                            : undefined
                        : car?.preis?.vkn,
            },
            scheckheft: car?.scheckheft === 'ja',
            siegel: car?.siegel?.[0],

            simc: await handleCarList({ cars: car?.simc?.filter((similar) => car?.id !== similar?.id) }, module, settings, faarenVehicles),
            sitze: car?.sitze,
            sonderangebot: car?.aktion === '1',
            standort: car?.standort,
            tsn: car?.tsn,
            tueren: car?.tueren,
            typ: car?.typ,
            unfall: car?.unfall,
            user: car?.user,
            users: car?.users,
            usersSelect,
            verbrauch,
            verfuegbarAb: !checkUndefinedNullOrEmpty(car?.verfuegbarAb) ? parse(car?.verfuegbarAb?.toString(), 'T', new Date()) : undefined,
            vin: car?.vin,
            vorbesitzer: car?.vorbesitzer,
        };
    }
}
